import { fetch } from '@mx/util/fetch';
import { toast, modal } from '@mx/vix';
import { analytics } from './analytics';
import { analyticConfig } from '@/config/analyticConfig';
import { bridge } from './bridge';
import { env } from './env';
import { addErrorCode } from './util';
import { monitor } from './monitor';
import encrypt from '@/common/encrypt';

interface AjaxSettings {
  url: string;
  data?: any;
  toastType?: string;
  toastText?: string;
  customErrorHandle?: boolean;
  noEncrypt?: boolean;
  customErrorModal?: any;
}

enum ErrorLevel {
  TOAST = 1,
  MODAL_EXIT,
  MODAL,
  EXIT,
  INLINE,
}
const errorUrl = ['/balance/donateOrder', '/balance/cashierUrl'];

class CustomError extends Error {
  level: ErrorLevel;

  code: number;

  extra: string;

  constructor(message: string | undefined, level: ErrorLevel, code?: number, extra?: string) {
    super(message);
    this.level = level;
    this.code = code || 0;
    this.extra = extra || '';
  }
}

export const ajax = async (settings: AjaxSettings, payLoadJson = false, options: any = {}): Promise<any> => {
  // loading提示
  // 默认显示 loading toast
  switch (settings.toastType) {
    case 'hide':
      break;
    default:
      toast.show('loading', settings.toastText || '加载中', {
        delay: 0,
      });
  }
  const data = await addParams(settings.data, settings.url);

  if (settings.toastType === 'show') {
    toast.hide();
    toast.show('text', data.loading_text, { delay: 0, duration: 1000 });
  }
  if (!settings.noEncrypt) {
    encrypt(data);
  }

  return fetch(settings.url, {
    data: options.toJson ? JSON.stringify(data) : data,
    credentials: options.credentials ? options.credentials : 'same-origin',
    headers: {
      'Content-Type': payLoadJson ? 'application/json' : 'application/x-www-form-urlencoded',
    },
  })
    .then((response) => {
      if (settings.customErrorHandle) {
        return response;
      }
      innerSuccess(response, settings.toastType);
      return response;
    })
    .catch((error: Error) => {
      monitor.captureAjaxErrorMessage(
        {
          name: `[api error] ${settings.url}`,
          msg: error?.message || '',
        },
        {
          errorCode: (error as any)?.code || '',
        }
      );
      if (errorUrl.indexOf(settings.url) !== -1) {
        error = new CustomError('系统繁忙，请稍后重试', ErrorLevel.TOAST);
      }
      if (settings.customErrorHandle) {
        throw error;
      }
      if (String((error as any)?.code) === '120010') {
        error = new CustomError(error.message || '系统繁忙，请稍后重试', ErrorLevel.TOAST);
      }
      if (!('level' in error)) {
        error = new CustomError('网络异常, 请检查您的网络设置', ErrorLevel.TOAST);
      }
      if ((error as any).code === '965001') {
        return { error };
      }
      errorHandler(error, settings.customErrorModal);
      throw error;
    });
};

async function getUuid() {
  // 风控需要根据后台传的uuid:nb_uuid获取到指纹信息，但是后台会在cookie中重新设置nb_uuid，所以将从cookie中获取改为从knb中获取
  const user = await bridge.getUserInfo();
  return user.uuid || '';
}

async function addParams(data: any, url: string) {
  let uuid = '';
  // 充值提现流程所需接口传参nb_uuid需从KNB中获取
  const urlArr = ['/balance/mainPage', '/balance/detailList', '/balance/detail', '/balance/transferOut'];
  if (urlArr.indexOf(url) !== -1) {
    uuid = env.uuid;
  } else {
    uuid = await getUuid();
  }
  let params = {
    lat: env.lat,
    lng: env.lng,
    stid: env.stId,
    uuid: uuid || env.uuid,
    from: env.entry,
    nb_app: env.app,
    nb_appversion: env.version,
    nb_deviceid: env.utmContent,
    nb_channel: env.utmSource,
    nb_ci: env.cityId,
    nb_platform: 'touch',
    nb_osversion: '',
    nb_uuid: uuid || env.nb_uuid,
    nb_source: env.nbSource,
    nb_version: '6.2.0',
    version: env.utmTerm,
    utm_term: env.utmTerm,
    utm_medium: env.utmMedium,
    utm_source: env.utmSource,
    utm_content: env.utmContent,
    utm_campaign: env.utmCampaign,
    nb_platform_risk: env.platform,
    ...data,
  };

  if (env.isWechat || env.isWXMiniProgram) {
    params = {
      // 微信 美团小程序cookie中无登录态信息，只有url中有。为了适配此场景，在接口传参中加上token
      token: env.token,
      ...params,
      nb_appid: env.wechatAppId,
      app_id: env.wechatAppId,
    };
  }
  const noLocationList = ['/balance/mainPage', '/balance/detailList', '/balance/withdrawStart'];
  if (noLocationList.indexOf(url) !== -1) {
    delete params.lat;
    delete params.lng;
  }
  return params;
}

// 请求成功回调函数
function innerSuccess(response: { error: any }, toastType: any): void {
  // 不需要toast信息时
  if (toastType !== 'show') {
    toast.hide();
  }
  if (!response) {
    throw new CustomError('网络异常, 请检查您的网络设置', ErrorLevel.TOAST);
  }
  if ('error' in response) {
    if (Number(response.error.level) < 5) {
      const error = response.error;
      throw new CustomError(error.message, error.level, error.code, error.extra || '');
    }
  }
}

// 错误处理
// level = 1, 显示 toast
// level = 3, 弹窗提示
// level = 2 或 4, 弹窗提示并退出页面
function errorHandler(error: any, customErrorModal?: any): void {
  toast.hide();

  switch (parseInt(error.code, 10)) {
    case 400000: // 获取立减金余额异常
      monitor.captureMessage('获取立减金余额异常');
      break;
    case 500011: // 登录信息异常
    case 500001:
    case 114001:
    case 500010: // 未登录
    case 10101: // 未登录
    case 401: // 登录失败
      bridge.login();
      return;
    default:
  }
  if (error.code === 1100020) {
    return;
  }
  // 如果有 extra 字段，解析并弹窗
  if (error.extra && error.extra !== 'null') {
    let extraData: Record<string, any> = {};

    try {
      extraData = JSON.parse(error.extra);
    } catch (parseErr) {
      // ...
    }

    // 产品充值限额：1100044
    // 产品提现限额：1100045
    // 风控提现限额：1100046
    const productionLimit = error.code === 1100044 || error.code === 1100045 || error.code === 1100046;
    // 充值风控限额
    const riskLimit = extraData?.popupType === 'rechargeRiskLimit';

    if (productionLimit || riskLimit) {
      analytics.mv(analyticConfig.riskModal.mv, {
        popup_type: '限额弹窗',
      });
      modal.show({
        preventScroll: true,
        title: extraData.title,
        content: extraData.body,
        button: {
          text: extraData.buttonText || '知道了',
          onClick: () => {
            modal.hide();
          },
        },
      });
      return;
    }

    // 管控内部户支持仅提现
    if (error.code === 1100077) {
      modal.show({
        preventScroll: true,
        title: extraData.title,
        content: extraData.body,
        button: {
          text: extraData.buttonText || '知道了',
          onClick: () => {
            modal.hide();
          },
        },
      });
      return;
    }
  }

  const errorLevel = parseInt(error.level, 10);
  switch (errorLevel) {
    case ErrorLevel.TOAST:
      setTimeout(() => {
        toast.show('text', addErrorCode(error.message, error.code, parseInt(error.level, 10)));
      }, 300);
      break;
    case ErrorLevel.MODAL_EXIT:
    case ErrorLevel.EXIT:
      modal.show({
        preventScroll: true,
        content: error.message,
        closeButton: !!customErrorModal?.closeButton,
        title: customErrorModal?.title || '',
        className: customErrorModal?.className || '',
        button: {
          text: '知道了',
          onClick: () => {
            modal.hide();
            bridge.closePage();
          },
        },
      });
      break;
    case ErrorLevel.MODAL:
      modal.show({
        preventScroll: true,
        content: error.message,
        button: {
          text: '知道了',
          onClick: () => {
            modal.hide();
          },
        },
      });
      break;
    default:
  }
}
